import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductCardGrid from "@stoneage/App/Components/ProductCards/ProductCardGrid";
import ProductCardNarrow from "@stoneage/App/Components/ProductCards/ProductCardNarrow";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
        productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    };
};

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    showImage: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showCompare: boolean;
    showAttributes: boolean;
    showAvailability: boolean;
    showTitle: boolean;
    extendedStyles?: ProductCarouselProductStyles;
    customActionsTemplate?: (product: ProductModel) => React.ReactNode;
    imageDimensions?: number;
    relatedProductsEventHandler?: (destination: string) => void;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    hiddenBelow?: InjectableCss;
    hiddenAbove?: InjectableCss;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    hiddenBelow: {
        css: css`
            height: 100%;
            @media (max-width: 1024px) {
                display: none;
            }
        `,
    },
    hiddenAbove: {
        css: css`
            display: none;
            @media (max-width: 1024px) {
                height: 100%;
                display: flex;
            }
        `,
    },
};

const ProductCarouselProduct: React.FC<Props> = ({
    carouselId,
    product,
    showImage,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showCompare,
    showAttributes,
    showAvailability,
    showTitle,
    isAuthenticated,
    updateProductInfo,
    extendedStyles,
    productInfo,
    imageDimensions,
    relatedProductsEventHandler,
}) => {
    if (!product || !productInfo) {
        return null;
    }

    const [styles] = React.useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const [isTouch, setIsTouch] = React.useState(false);

    React.useEffect(() => {
        // detecting if device is in touch mode to add the dragable ability for product carousel
        if ("ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.maxTouchPoints > 0) {
            setIsTouch(true);
        }
    }, []);

    return (
        <StyledWrapper className={`product-card ${isTouch ? "" : "no-swiping-elements"}`}>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper {...styles.hiddenBelow}>
                    <ProductCardGrid
                        product={product}
                        productInfo={productInfo}
                        showImage={showImage}
                        showCompare={showCompare}
                        showAttributes={showAttributes}
                        showAvailability={showAvailability}
                        showPartNumbers={showPartNumbers}
                        showTitle={showTitle}
                        showPrice={showPrice}
                        showAddToCart={showAddToCart}
                        isAuthenticated={isAuthenticated}
                        imageDimensions={imageDimensions}
                        view="Grid"
                        relatedProductsEventHandler={relatedProductsEventHandler}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.hiddenAbove}>
                    <ProductCardNarrow
                        showImage={showImage}
                        showCompare={showCompare}
                        showAttributes={showAttributes}
                        showAvailability={showAvailability}
                        showPartNumbers={showPartNumbers}
                        showTitle={showTitle}
                        showPrice={showPrice}
                        showAddToCart={showAddToCart}
                        relatedProductsEventHandler={relatedProductsEventHandler}
                    />
                </StyledWrapper>
            </ProductContext.Provider>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
